/**
 * Switch-list telemetry wire types (Plan 26 Phase 0c).
 *
 * Mirrors the backend role-split DTOs:
 *   CustomerTelemetryDTO     — every non-SUPERADMIN role
 *   InternalSolverTraceDTO   — SUPERADMIN only (never served otherwise)
 *
 * The union's `engineVersion` field is the discriminator — it only exists on
 * the internal trace. Do not add solver-internal fields to CustomerTelemetry.
 */

export type MetricTag = 'MEASURED' | 'MODELED' | 'ESTIMATED'

/** Tagged metric value as serialized by SwitchListTelemetryWriter. */
export interface TaggedMetric {
  value: number
  tag: MetricTag
}

/** One structured reason object emitted by a pipeline strategy. */
export interface TelemetryReason {
  code: string
  severity: 'ERROR' | 'WARNING' | 'INFO'
  taskId?: string
  trackId?: string
  humanText: string
}

/** CustomerTelemetryDTO — permitted fields only. */
export interface CustomerTelemetry {
  workOrderId: string
  generationVersion: number
  executionStatus: 'SUCCESS' | 'BLOCKED' | 'FAILED'
  measuredMetrics?: Record<string, TaggedMetric>
  modeledMetrics?: Record<string, TaggedMetric>
  reasonCodes: TelemetryReason[]
  dataCompletenessRatio?: number
  createdAt: string
}

/** InternalSolverTraceDTO — SUPERADMIN only; full ledger row. */
export interface InternalSolverTrace extends Omit<CustomerTelemetry, 'reasonCodes' | 'dataCompletenessRatio' | 'executionStatus'> {
  id: string
  facilityId: string
  engineVersion: string
  payloadHash: string
  reasonCodes: TelemetryReason[]
  executionStatus: 'SUCCESS' | 'BLOCKED' | 'FAILED'
  errorDiagnostic?: Record<string, unknown>
  createdBy?: string
}

export type SwitchJobTelemetry = CustomerTelemetry | InternalSolverTrace

/** True when the row came back as the SUPERADMIN internal trace. */
export function isInternalTrace(t: SwitchJobTelemetry): t is InternalSolverTrace {
  return 'engineVersion' in t && t.engineVersion != null
}
